/*
 * retrieveToken: 從app拿取token
 * retrieveRefreshToken: 從app拿取refresh token
 * setTokenToNative: 將token更新至app
 * goToReservationDetail: 關掉webview進入native訂單頁面
 * presentMessenger: 開啟native客服對話
 * logFirebaseEvent: 呼叫App發送firebase data
 */

import { isNil } from 'lodash-es'

export const invokeNativeAPP = (apiName, data) => {
  if (typeof window === 'undefined') return
  const string =
    isNil(data) || typeof data === 'string' ? data : JSON.stringify(data)
  try {
    return window.webkit.messageHandlers[apiName].postMessage(string) // ios
  } catch (err) {
    // android
    if (window.callback?.[apiName]) {
      if (string) {
        return window.callback[apiName](string)
      } else {
        return window.callback[apiName]()
      }
    }
  }
}

// return value is string
const readFromNative = (apiName, options) => {
  const { data, timeout } = options || {}

  if (window.callback?.[apiName]) {
    // run into this block when android callback exist
    return invokeNativeAPP(apiName, data)
  } else {
    // ios
    return new Promise((resolve) => {
      const callbackNameWithoutWindow = `__${apiName}Callback`
      window[callbackNameWithoutWindow] = (result) => resolve(result)
      const callbackName = `window.${callbackNameWithoutWindow}`
      const dataSend =
        data !== undefined ? { data, callbackName } : callbackName
      invokeNativeAPP(apiName, dataSend)

      // 避免未實作API永遠block住，設定timeout
      setTimeout(() => resolve(), (timeout || 20) * 1000)
    })
  }
}

// return value is JSON Object
const readJsonFromNative = async (apiName, options) => {
  const result = await readFromNative(apiName, options)

  // 若為無法 parse 的值則避免解析錯誤並回傳 null 讓後續函式接手判斷
  if (isNil(result) || result === '') return null

  try {
    return JSON.parse(result)
  } catch (e) {
    console.error(
      'readJsonFromNative parse error:',
      e,
      'original text:',
      result
    )
    return null
  }
}

export const retrieveToken = () => readFromNative('getToken')
export const retrieveRefreshToken = () => readFromNative('getRefreshToken')
export const retrieveAppId = () => readFromNative('getAppId')
export const retrieveStopReminderValidateDealCustomer = () =>
  readFromNative('getStopReminderValidateDealCustomer')
export const setStopReminderValidateDealCustomerToNative = (bool) =>
  invokeNativeAPP('setStopReminderValidateDealCustomer', bool)

export const getApplePayToken = (amount) =>
  readFromNative('getApplePayToken', { data: amount, timeout: 15 * 60 })
export const setApplePayResult = (bool) =>
  invokeNativeAPP('setApplePayResult', bool)
export const checkNativeApplePayAvailable = () =>
  readJsonFromNative('getCanMakePaymentsWithApplePay')

export const setTokenToNative = (token) => invokeNativeAPP('setToken', token)
export const setRefreshTokenToNative = (token) => {
  invokeNativeAPP('setRefreshToken', token)
}

export const goToReservationDetail = (reservationId, reservationListMode) => {
  if (reservationListMode === 'stateUnordered') {
    invokeNativeAPP('goTo', {
      page: 'unorderedReservationDetail',
      param: { unorderedReservationId: reservationId },
    })
  } else {
    invokeNativeAPP('goTo', {
      page: 'reservationDetail',
      param: { reservationId },
    })
  }
}

export const presentMessenger = () => invokeNativeAPP('presentMessenger')

export const closeWebView = () => invokeNativeAPP('closeWebView', true)

export const loadAuthDataFromLocalbondNative = () => {
  return new Promise((resolve) => {
    window.__setCodedData = (token) => resolve(token)
    invokeNativeAPP('getCodedData00', 'window.__setCodedData')
  })
}

export const enableDragReload = () => invokeNativeAPP('allowReload', false)

export const enableNativeHeaderMainTabs = (bool) =>
  invokeNativeAPP('showWebViewMainTabs', bool)
export const enableNativeHeaderNavBack = (bool) =>
  invokeNativeAPP('showBackButton', bool)
export const enableNativeHeaderSearch = (bool) =>
  invokeNativeAPP('showSearchButton', bool)

export const shareUrlFromNative = (content, url) => {
  invokeNativeAPP('shareUrl', { content, url })
}

export const copyTextFromNative = (text) => invokeNativeAPP('copy', text)
